import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  computeTodayFocus,
  type FocusEvent as FocusEventRow,
  type FocusAction,
} from "@/lib/today-focus";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Flame,
  Timer,
  Target,
  ArrowRight,
  GraduationCap,
  Layers,
  ListChecks,
  Compass,
  ChevronDown,
} from "lucide-react";
import { motion } from "framer-motion";
import { ResumeCard } from "@/components/ResumeCard";
import { TomorrowPrepCard } from "@/components/TomorrowPrepCard";
import { HomeWidgets } from "@/components/widgets/HomeWidgets";
import { LumenQuestBoard } from "@/components/LumenQuestBoard";
import { ClassHomeworkCard } from "@/components/ClassHomeworkCard";
import { ClassAnnouncementsCard } from "@/components/ClassAnnouncementsCard";
import { AssignmentAlertsCard } from "@/components/AssignmentAlertsCard";
import { TeachingAlertsCard } from "@/components/TeachingAlertsCard";

export const Route = createFileRoute("/_app/")({
  head: () => ({
    meta: [
      { title: "Il tuo studio di oggi — Lumen" },
      {
        name: "description",
        content:
          "Dashboard di studio Lumen: obiettivo giornaliero, sessione, tutor AI, mappe, flashcard, quiz e statistiche.",
      },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: HomePage,
});

type Profile = { full_name: string | null; streak_days: number; total_xp: number; level: number };

function greetingKey() {
  const h = new Date().getHours();
  if (h < 6) return "home.greeting.night";
  if (h < 13) return "home.greeting.morning";
  if (h < 19) return "home.greeting.afternoon";
  return "home.greeting.evening";
}

const DAILY_QUOTE_KEYS = Array.from({ length: 31 }, (_, i) => `home.quotes.${i}`);

function dailyQuoteKey() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now.getTime() - start.getTime()) / 86400000);
  const seed = dayOfYear + now.getFullYear() * 366;
  return DAILY_QUOTE_KEYS[seed % DAILY_QUOTE_KEYS.length];
}

function MotivationalQuote() {
  const { t } = useTranslation();
  const key = dailyQuoteKey();
  const text = t(`${key}.text`);
  const bold = t(`${key}.bold`);
  const [before, after] = text.split("{b}");
  return (
    <p className="mt-3 text-sm text-foreground/80">
      {before}
      <b>{bold}</b>
      {after}
    </p>
  );
}

function HomePage() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [minutesToday, setMinutesToday] = useState(0);
  const [events, setEvents] = useState<FocusEventRow[]>([]);
  const [dueCards, setDueCards] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const dailyTarget = 90;

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data } = await supabase
        .from("profiles")
        .select("full_name,streak_days,total_xp,level")
        .eq("id", u.user.id)
        .maybeSingle();
      setProfile(data as Profile | null);
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const { data: sess } = await supabase
        .from("study_sessions")
        .select("duration_minutes")
        .gte("started_at", start.toISOString());
      const total = (sess ?? []).reduce((a, s) => a + (s.duration_minutes ?? 0), 0);
      setMinutesToday(total);
      const { data: ev } = await supabase
        .from("calendar_events")
        .select("title,event_type,starts_at,subject_name,is_done")
        .gte("starts_at", start.toISOString())
        .order("starts_at", { ascending: true })
        .limit(30);
      setEvents((ev ?? []) as FocusEventRow[]);
      const { count } = await supabase
        .from("flashcards")
        .select("id", { count: "exact", head: true })
        .lte("next_review_at", new Date().toISOString());
      setDueCards(count ?? 0);
    })();
  }, []);

  const focus = useMemo(
    () => computeTodayFocus({ events, dueFlashcards: dueCards, minutesToday, dailyTarget }),
    [events, dueCards, minutesToday],
  );

  const firstName = profile?.full_name?.split(" ")[0] ?? t("home.defaultStudent");
  const pct = Math.min(100, Math.round((minutesToday / dailyTarget) * 100));

  return (
    <div className="space-y-5 px-5 pt-8">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3"
      >
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{t(greetingKey())}</p>
          <h1 className="truncate text-2xl font-bold tracking-tight">
            {t("home.title", { name: firstName })}
          </h1>
        </div>
        <div className="pastel-peach flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-foreground">
          <Flame aria-hidden="true" className="h-4 w-4" />
          <span>{t("home.streakDays", { count: profile?.streak_days ?? 0 })}</span>
        </div>
      </motion.header>

      {/* Azione di oggi: obiettivo + avvio sessione in un unico blocco prioritario */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <Card className="relative overflow-hidden border-0 bg-primary p-5 text-primary-foreground shadow-[var(--shadow-soft)]">
          <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-primary-foreground/10 blur-2xl" />
          <div className="relative">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
              <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold opacity-90">
                <Target className="h-4 w-4 shrink-0" />{" "}
                <span className="truncate">{t("home.dailyGoal")}</span>
              </h2>
              <span className="shrink-0 text-xs font-medium tabular-nums opacity-80">
                {minutesToday} / {dailyTarget} min
              </span>
            </div>
            <Progress
              value={pct}
              aria-label={t("home.dailyGoal")}
              aria-valuetext={`${minutesToday} / ${dailyTarget} min`}
              className="mt-3 h-2 bg-primary-foreground/25 [&>div]:bg-primary-foreground"
            />
            <div className="mt-1 text-sm text-primary-foreground/90">
              <MotivationalQuote />
            </div>
            <Link to="/studio" className="mt-4 block rounded-2xl">
              <span className="group grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl bg-primary-foreground/15 p-3 text-left transition-colors hover:bg-primary-foreground/25 active:scale-[0.99]">
                <span className="rounded-xl bg-primary-foreground/20 p-2">
                  <Timer className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs uppercase tracking-wide opacity-80">
                    {t("home.session")}
                  </span>
                  <span className="block truncate text-base font-semibold">
                    {t("home.startStudying")}
                  </span>
                </span>
                <ArrowRight
                  aria-hidden="true"
                  className="h-5 w-5 opacity-80 transition-transform group-hover:translate-x-1"
                />
              </span>
            </Link>
          </div>
        </Card>
      </motion.div>

      {/* Oggi: al massimo 3 azioni concrete, non un menù di destinazioni */}
      <section aria-labelledby="today-focus" className="space-y-2">
        <div className="px-1">
          <h2 id="today-focus" className="section-title">
            {t("today.title")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t("today.subtitle", { count: focus.length })}
          </p>
        </div>
        {focus.map((action, i) => (
          <FocusCard key={action.kind} action={action} delay={0.05 * i} />
        ))}
      </section>

      <ResumeCard />

      <LumenQuestBoard />

      {/* Tutto il contorno (avvisi, compiti di classe, widget) sta chiuso di default:
          la home resta su poche azioni concrete. */}
      <section aria-labelledby="today-more">
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          aria-expanded={showMore}
          className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-border/60 bg-card px-4 py-3 text-left transition-colors hover:bg-accent/40"
        >
          <span className="min-w-0">
            <span id="today-more" className="block truncate text-sm font-semibold">
              {t("today.more")}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {t("today.moreSub")}
            </span>
          </span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${showMore ? "rotate-180" : ""}`}
          />
        </button>

        {showMore && (
          <div className="mt-3 space-y-4">
            <h3 className="sr-only">{t("home.studyTools")}</h3>
            <ClassHomeworkCard />
            <ClassAnnouncementsCard />
            <AssignmentAlertsCard />
            <TeachingAlertsCard />
            <TomorrowPrepCard />
            <HomeWidgets />
          </div>
        )}
      </section>


      {/* Tutto il resto resta raggiungibile da un'unica voce "Esplora" */}
      <Link
        to="/explore"
        className="block rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
      >
        <Card className="tone-lilac grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 p-4 transition-shadow hover:shadow-[var(--shadow-soft)] active:scale-[0.99]">
          <span className="rounded-2xl bg-background/70 p-2.5 text-primary">
            <Compass aria-hidden="true" className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">{t("today.exploreCta")}</span>
            <span className="block truncate text-xs text-foreground/60">
              {t("explore.subtitle")}
            </span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0 text-foreground/50" />
        </Card>
      </Link>
    </div>
  );
}

const FOCUS_TONE: Record<FocusAction["tone"], string> = {
  sky: "tone-sky",
  sage: "tone-sage",
  lilac: "tone-lilac",
  peach: "tone-peach",
};

const FOCUS_ICON: Record<FocusAction["kind"], typeof Target> = {
  exam: Target,
  homework: ListChecks,
  review: Layers,
  session: Timer,
  method: GraduationCap,
};

function FocusCard({ action, delay }: { action: FocusAction; delay: number }) {
  const { t } = useTranslation();
  const Icon = FOCUS_ICON[action.kind];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <Link
        to={action.to as never}
        className="block rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
      >
        <Card
          className={`${FOCUS_TONE[action.tone]} grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 p-4 transition-shadow hover:shadow-[var(--shadow-soft)] active:scale-[0.99]`}
        >
          <span className="rounded-2xl bg-background/70 p-2.5 text-primary">
            <Icon aria-hidden="true" className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">
              {t(`today.actions.${action.kind}.title`, action.values ?? {})}
            </span>
            <span className="block truncate text-xs text-foreground/70">
              {t(`today.actions.${action.kind}.sub`, action.values ?? {})}
            </span>
          </span>
          <span className="shrink-0 rounded-full bg-background/70 px-3 py-1 text-xs font-semibold">
            {t(`today.actions.${action.kind}.cta`)}
          </span>
        </Card>
      </Link>
    </motion.div>
  );
}
